/**
 * TOPBAR - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - Individual glass buttons (like macOS Finder - NOT full-width panel)
 * - Liquid Glass material with lensing and refraction
 * - Morphing interactive elements with spring physics
 * - Specular highlights and energize effects
 * - Expandable search with fluid morphing animations
 * - Animated page title transitions
 */

import { useState, useEffect } from 'react'
import { Search, ChevronLeft, ChevronRight, Grid3x3, List, MoreHorizontal, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

const pageNames: Record<string, string> = {
  '/': 'Dashboard',
  '/my-portal': 'My Portal',
  '/advances': 'Advances',
  '/partners': 'Partners',
  '/clients': 'Clients',
  '/transactions': 'Transactions',
  '/pool': 'Pool Balance',
  '/distributions': 'Distributions',
  '/reserves': 'Reserves',
  '/monthly-closing': 'Monthly Closing',
  '/wise': 'Wise Sync',
  '/reports': 'Reports',
  '/settings': 'Settings',
}

// Spring config for buttons
const buttonSpring = {
  type: "spring" as const,
  damping: 20,
  stiffness: 400
}

export function Topbar() {
  const location = useLocation()
  const currentPage = pageNames[location.pathname] || 'Dashboard'
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const handleBack = () => {
    window.history.back()
  }

  const handleForward = () => {
    window.history.forward()
  }

  // Close search on escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchExpanded) {
        setSearchExpanded(false)
        setSearchQuery('')
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [searchExpanded])

  return (
    <motion.div
      className="flex-shrink-0 h-[64px] flex items-center justify-between px-6 gap-4"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
    >
      {/* Left: Navigation Buttons (Grouped) + Title */}
      <div className="flex items-center gap-5">
        {/* Navigation Buttons - Glass container with EXTREME blur like macOS */}
        <div className="flex items-center gap-0 liquid-material-regular liquid-lens rounded-2xl shadow-glass dark:shadow-dark-glass overflow-hidden">
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.02, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            whileTap={{ scale: 0.95 }}
            transition={buttonSpring}
            className="w-[56px] h-[40px] flex items-center justify-center group relative"
          >
            <ChevronLeft className="w-[18px] h-[18px] text-light-text-primary dark:text-dark-text-primary group-hover:text-blue dark:group-hover:text-blue-dark transition-colors duration-fast" strokeWidth={2.5} />
            {/* Subtle internal separator */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-px bg-black/[0.08] dark:bg-white/[0.08]" />
          </motion.button>
          <motion.button
            onClick={handleForward}
            whileHover={{ scale: 1.02, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
            whileTap={{ scale: 0.95 }}
            transition={buttonSpring}
            className="w-[56px] h-[40px] flex items-center justify-center group"
          >
            <ChevronRight className="w-[18px] h-[18px] text-light-text-primary dark:text-dark-text-primary group-hover:text-blue dark:group-hover:text-blue-dark transition-colors duration-fast" strokeWidth={2.5} />
          </motion.button>
        </div>

        {/* Page Title with animated transition */}
        <AnimatePresence mode="wait">
          <motion.h2
            key={currentPage}
            className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {currentPage}
          </motion.h2>
        </AnimatePresence>
      </div>

      {/* Right: View Controls + Search */}
      <div className="flex items-center gap-3">
        {/* View Options - Glass container with toggle indicator */}
        <div className="flex items-center gap-0 liquid-material-regular liquid-lens rounded-2xl shadow-glass dark:shadow-dark-glass overflow-hidden relative">
          {/* Animated background indicator */}
          <motion.div
            className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-blue/10 dark:bg-blue-dark/10 rounded-xl"
            animate={{ x: viewMode === 'grid' ? 2 : 'calc(100% + 4px)' }}
            transition={buttonSpring}
          />

          <motion.button
            onClick={() => setViewMode('grid')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            transition={buttonSpring}
            className="w-[56px] h-[40px] flex items-center justify-center group relative z-10"
          >
            <Grid3x3
              className={cn(
                "w-[17px] h-[17px] transition-colors duration-fast",
                viewMode === 'grid'
                  ? "text-blue dark:text-blue-dark"
                  : "text-light-text-secondary dark:text-dark-text-secondary group-hover:text-light-text-primary"
              )}
              strokeWidth={2.5}
            />
            {/* Subtle internal separator */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-px bg-black/[0.08] dark:bg-white/[0.08]" />
          </motion.button>
          <motion.button
            onClick={() => setViewMode('list')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            transition={buttonSpring}
            className="w-[56px] h-[40px] flex items-center justify-center group relative z-10"
          >
            <List
              className={cn(
                "w-[17px] h-[17px] transition-colors duration-fast",
                viewMode === 'list'
                  ? "text-blue dark:text-blue-dark"
                  : "text-light-text-secondary dark:text-dark-text-secondary group-hover:text-light-text-primary"
              )}
              strokeWidth={2.5}
            />
          </motion.button>
        </div>

        {/* More Options - Glass button */}
        <motion.button
          whileHover={{ scale: 1.05, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}
          whileTap={{ scale: 0.95 }}
          transition={buttonSpring}
          className="w-[56px] h-[40px] flex items-center justify-center rounded-2xl liquid-material-regular liquid-lens shadow-glass dark:shadow-dark-glass group"
        >
          <MoreHorizontal className="w-[18px] h-[18px] text-light-text-primary dark:text-dark-text-primary group-hover:text-blue dark:group-hover:text-blue-dark transition-colors duration-fast" strokeWidth={2.5} />
        </motion.button>

        {/* Search - Expandable with morphing animation */}
        <motion.div
          animate={{
            width: searchExpanded ? 320 : 56,
            boxShadow: searchExpanded
              ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 3px rgba(0, 122, 255, 0.1)'
              : '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
          transition={{
            type: "spring",
            damping: 25,
            stiffness: 300
          }}
          className="relative flex items-center h-[40px] liquid-material-regular liquid-lens rounded-2xl overflow-hidden"
        >
          <motion.button
            onClick={() => setSearchExpanded(!searchExpanded)}
            whileHover={!searchExpanded ? { scale: 1.05 } : undefined}
            whileTap={{ scale: 0.95 }}
            transition={buttonSpring}
            className="flex items-center justify-center flex-shrink-0 w-[56px] h-[40px] group"
          >
            <Search
              className={cn(
                "w-[17px] h-[17px] transition-colors duration-fast",
                searchExpanded
                  ? "text-blue dark:text-blue-dark"
                  : "text-light-text-primary dark:text-dark-text-primary group-hover:text-blue dark:group-hover:text-blue-dark"
              )}
              strokeWidth={2.5}
            />
          </motion.button>

          <AnimatePresence>
            {searchExpanded && (
              <>
                <motion.input
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ delay: 0.05, type: "spring", damping: 20, stiffness: 300 }}
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="flex-1 bg-transparent border-none text-subheadline text-light-text-primary dark:text-dark-text-primary placeholder:text-light-text-tertiary dark:placeholder:text-dark-text-tertiary focus:outline-none tracking-tight"
                />
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setSearchQuery('')}
                    className="mr-3 p-1 rounded-full hover:bg-black/[0.05] dark:hover:bg-white/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4 text-light-text-tertiary" strokeWidth={2} />
                  </motion.button>
                )}
              </>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  )
}
